import { ExpertiseData, UrgencyInfo } from '../types/pipeline.types';

export const calculateUrgency = (expertise: ExpertiseData): UrgencyInfo => {
  const now = new Date();
  const referenceDate = expertise.date ? new Date(expertise.date) : now;
  
  const diffMs = now.getTime() - referenceDate.getTime();
  const totalHours = Math.floor(diffMs / (1000 * 60 * 60));
  const daysSince = Math.floor(totalHours / 24);
  const hoursSince = totalHours % 24;
  
  let isUrgent = false;
  let reason = '';
  
  // Stage-specific urgency thresholds
  if (expertise.stage === 10) {
    // Bureau à encoder: urgent after 24h
    isUrgent = totalHours >= 24;
    reason = isUrgent ? 'Plus de 24h depuis création' : '';
  } else if (expertise.stage === 20) {
    // Bureau à planifier: urgent after 48h
    isUrgent = totalHours >= 48;
    reason = isUrgent ? 'Plus de 48h sans planification' : '';
  } else if (expertise.stage === 30) {
    // RV en attente: no urgent label
    isUrgent = false;
  } else if ([40, 50, 60, 70, 110].includes(expertise.stage || 0)) {
    // Expert à visiter, Expert à achever, Bureau à valoriser, Bureau à achever, À transmettre
    // Use default urgency (7 days)
    isUrgent = daysSince >= 7;
    reason = isUrgent ? `${daysSince} jours écoulés` : '';
  } else if ([80, 90, 100].includes(expertise.stage || 0)) {
    // Attente categories: no urgent label
    isUrgent = false;
  }
  
  return {
    isUrgent,
    totalHours,
    daysSince,
    hoursSince,
    reason
  };
};

export const calculateTimeSince = (expertise: ExpertiseData): string => {
  const urgencyInfo = expertise.urgency || calculateUrgency(expertise);
  const status = expertise.status?.toLowerCase() || '';
  const normalizedStatus = status.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const isExpertVisiter = normalizedStatus.includes('expert a visiter') || normalizedStatus.includes('expert à visiter');
  
  // Special handling for Expert à visiter
  if (isExpertVisiter) {
    if (!expertise.date_du_rv) {
      return 'Pas de RV';
    }
    
    const rvDate = new Date(expertise.date_du_rv);
    const now = new Date();
    const diffMs = rvDate.getTime() - now.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return `RV dépassé ${Math.abs(diffDays)}j`;
    } else if (diffDays === 0) {
      return 'RV aujourd\'hui';
    } else {
      return `RV dans ${diffDays}j`;
    }
  }
  
  // For all other categories
  if (urgencyInfo.totalHours < 24 && urgencyInfo.daysSince === 0) {
    return 'Nouveau';
  } else if (urgencyInfo.daysSince > 0) {
    let timeSince = `${urgencyInfo.daysSince}j`;
    if (urgencyInfo.hoursSince > 0) {
      timeSince += ` ${urgencyInfo.hoursSince}h`;
    }
    return timeSince;
  } else if (urgencyInfo.totalHours > 0) {
    return `${Math.floor(urgencyInfo.totalHours)}h`;
  }
  
  return '0h';
};

export const sortByUrgencyCategory = (expertises: ExpertiseData[]): ExpertiseData[] => {
  return [...expertises].sort((a, b) => {
    // First sort by urgency
    const aUrgent = a.isUrgent || a.urgency?.isUrgent || false;
    const bUrgent = b.isUrgent || b.urgency?.isUrgent || false;
    
    if (aUrgent && !bUrgent) return -1;
    if (!aUrgent && bUrgent) return 1;
    
    // Then by total hours
    const aHours = a.urgency?.totalHours || 0;
    const bHours = b.urgency?.totalHours || 0;
    
    return bHours - aHours;
  });
};